/**
 * The change-feed push socket (`docs/realtime-updates-plan-v1.md` Phase 7c).
 *
 * ## This is a DOORBELL, not a data source
 *
 * The socket's only job is to say "something changed, go look". It never carries state into the app: every
 * event calls the same `onRing` the focus and heartbeat signals call, which re-reads the durable cursor via
 * `GET /changes` and runs the one tested invalidation path. That is a deliberate deviation from the plan's
 * "the `{domain, rev}` contract IS the socket frame" — the frame does carry that shape, we just refuse to
 * act on it — and it buys four properties that are worth one ~200-byte request:
 *
 * 1. **A forged or replayed event cannot lie about state.** Publish is IAM-only (`RealtimeStack`), so a
 *    signed-in user cannot publish at all; but if that were ever widened, the worst a bogus event could do
 *    here is cause one extra cursor read.
 * 2. **Dropped and out-of-order events are self-correcting.** AWS ships an `InboundMessageDropped` metric
 *    precisely because broadcasts can be lost. Acting on frame contents would need a monotonic guard per
 *    domain to survive reordering; reading the cursor is absolute state, so neither case can strand us.
 * 3. **The `feedEnabled` kill-switch still governs.** Applying frames directly would need a second switch.
 * 4. **The money-path guard cannot be bypassed.** `MARK_STALE_ONLY` (the press worklist — refetching it
 *    under a staffer's finger can move a tap onto the wrong garment and fire `settleLine`) lives in that
 *    one path. A second invalidation path would be a second place to forget it.
 *
 * ## Why this owns the connection lifecycle instead of leaning on Amplify's reconnect
 *
 * ⚠️ `authToken` is a STATIC STRING in Amplify's provider options, captured at connect time. Amplify's own
 * reconnect logic reuses it, so an ID token that expires after an hour would be replayed forever — and the
 * shop's failure mode is a wall-mounted iPad that nobody touches for a week. `authMode: 'userPool'` avoids
 * that by calling `fetchAuthSession()` per connect, but it sends the **ACCESS** token (verified in
 * `@aws-amplify/api-graphql`'s `authHeaders`: `userPool → awsAuthTokenHeader → tokens.accessToken`), and
 * whether AppSync's Cognito authorizer accepts an access token is the one question `RealtimeStack` was
 * deployed to answer and which cannot be answered from source. Sending the ID token explicitly sidesteps
 * the question entirely — the ID token is the documented input to that authorizer, and it is the same token
 * the REST client already sends.
 *
 * So we reconnect on our own clock with a freshly fetched token. One mechanism, three jobs: token
 * freshness, plan **R7** (bound deauthorization latency on a live subscription to ≤15 min rather than the
 * connection's natural ~24 h), and recovery from a silent half-open socket — ⚠️ Safari before 17.3 does not
 * fire `onclose` on network loss, which is exactly the old shop iPads on exactly the flaky shop WiFi, so a
 * socket that believes it is healthy is a real state here and only a timer escapes it.
 *
 * Cost: AppSync Events bills per connection-minute, and recycling keeps the same steady-state minutes. At
 * ~5 tablets on all day it is cents per month.
 */
import type { ChangeFeedResponse } from '@fsp/contracts';

/** The socket config the server advertises. Absent ⇒ do not connect (see the contract's `push` docs). */
export type ChangePushTarget = NonNullable<ChangeFeedResponse['push']>;

/** Minimal shape of an Amplify `EventsChannel`, so the lifecycle is testable without Amplify. */
export type ChannelLike = {
  subscribe(
    observer: { next: (data: unknown) => void; error: (err: unknown) => void },
    options?: { authMode?: string; authToken?: string },
  ): { unsubscribe(): void };
  close(): void;
};

/** Opens one channel. Injected so tests drive the state machine directly. */
export type ChannelConnector = (args: {
  target: ChangePushTarget;
  idToken: string;
}) => Promise<ChannelLike>;

export type ChangeSocketDeps = {
  target: ChangePushTarget;
  /** Resolves the CURRENT ID token; called before every connect so a recycle refreshes it. */
  getIdToken: () => Promise<string | undefined>;
  /** An event arrived — go re-read the cursor. */
  onRing: () => void;
  /**
   * A connection is live. Fires on the FIRST connect and on every reconnect, and must trigger a cursor
   * read: events published while we were disconnected are simply gone, so the socket alone cannot be a
   * correctness floor. This is the "resync on reconnect" the plan requires.
   */
  onConnected: () => void;
  /** Gave up (auth is gone, or too many consecutive failures). Nothing further will be attempted. */
  onGaveUp?: (reason: string) => void;
  connect: ChannelConnector;
  /** Injectable for tests; defaults to the real timers. */
  setTimeout?: (fn: () => void, ms: number) => unknown;
  clearTimeout?: (handle: unknown) => void;
};

/**
 * Recycle interval. Must stay ≤15 min to satisfy R7's deauthorization bound, and comfortably under the
 * Cognito ID token's 60-minute life so a connect never presents a nearly-expired token.
 */
export const RECYCLE_MS = 12 * 60_000;

/**
 * Consecutive-failure cap, then stop (plan **R8**). The failure this exists for is a month-old kiosk
 * whose refresh token has died: without a cap it becomes a permanent connect-storm against a service that
 * will never say yes, and only 5xx is alarmed today, so nobody would find out.
 */
export const MAX_CONSECUTIVE_FAILURES = 5;

/** Capped exponential backoff, in ms, indexed by consecutive failure count. */
export function backoffMs(failures: number): number {
  return Math.min(1_000 * 2 ** Math.max(0, failures - 1), 30_000);
}

export type ChangeSocket = {
  /** Idempotent. */
  start(): void;
  /** Idempotent; safe to call from a React cleanup. Nothing fires after it. */
  stop(): void;
};

export function createChangeSocket(deps: ChangeSocketDeps): ChangeSocket {
  const schedule = deps.setTimeout ?? ((fn, ms) => globalThis.setTimeout(fn, ms));
  const unschedule = deps.clearTimeout ?? ((h) => globalThis.clearTimeout(h as never));

  let started = false;
  let stopped = false;
  let failures = 0;
  let channel: ChannelLike | undefined;
  let subscription: { unsubscribe(): void } | undefined;
  let timer: unknown;
  /**
   * Guards against a late async connect resolving after a newer cycle began — otherwise a slow connect
   * during a recycle leaves TWO live channels, doubling billed connection-minutes and double-ringing.
   */
  let generation = 0;

  const clearTimer = () => {
    if (timer !== undefined) {
      unschedule(timer);
      timer = undefined;
    }
  };

  const teardown = () => {
    // Unsubscribe BEFORE close: closing first can surface the teardown as a subscription `error`, which
    // would look like a connection failure and burn a retry.
    try {
      subscription?.unsubscribe();
    } catch {
      // A channel already gone is not a failure worth propagating.
    }
    try {
      channel?.close();
    } catch {
      // Ditto.
    }
    subscription = undefined;
    channel = undefined;
  };

  const giveUp = (reason: string) => {
    stopped = true;
    clearTimer();
    teardown();
    deps.onGaveUp?.(reason);
  };

  const cycle = async (): Promise<void> => {
    if (stopped) return;
    const myGeneration = ++generation;

    const idToken = await deps.getIdToken();
    if (stopped || myGeneration !== generation) return;
    if (!idToken) {
      // No token means signed out or a dead refresh token. Retrying cannot fix it and the auth layer owns
      // the remedy, so stop rather than become an unauthenticated request generator (R8).
      giveUp('no-id-token');
      return;
    }

    let opened: ChannelLike;
    try {
      opened = await deps.connect({ target: deps.target, idToken });
    } catch (err) {
      if (stopped || myGeneration !== generation) return;
      failures += 1;
      if (failures >= MAX_CONSECUTIVE_FAILURES) {
        giveUp(`connect-failed-${failures}: ${err instanceof Error ? err.message : String(err)}`);
        return;
      }
      timer = schedule(() => void cycle(), backoffMs(failures));
      return;
    }

    // A stop() or a newer cycle landed while connecting: close what we opened rather than leaking it.
    if (stopped || myGeneration !== generation) {
      try {
        opened.close();
      } catch {
        // nothing to do
      }
      return;
    }

    channel = opened;
    failures = 0;

    subscription = opened.subscribe(
      {
        next: () => {
          if (!stopped && myGeneration === generation) deps.onRing();
        },
        error: () => {
          // A subscription error means this connection is done. Reconnect on the backoff ladder rather
          // than assuming the socket recovers — for Safari <17.3 it may never tell us again.
          if (stopped || myGeneration !== generation) return;
          clearTimer();
          teardown();
          failures += 1;
          if (failures >= MAX_CONSECUTIVE_FAILURES) {
            giveUp(`subscribe-error-${failures}`);
            return;
          }
          timer = schedule(() => void cycle(), backoffMs(failures));
        },
      },
      // Repeated on subscribe as well as connect: Amplify resolves auth for the subscribe message
      // separately, and omitting it here would fall back to the configured default mode.
      { authMode: 'none', authToken: idToken },
    );

    // Resync AFTER subscribing, never before: a cursor read that completes while we are not yet listening
    // leaves a window whose events are lost with nothing to reveal it.
    deps.onConnected();

    timer = schedule(() => {
      if (stopped) return;
      teardown();
      void cycle();
    }, RECYCLE_MS);
  };

  return {
    start() {
      if (started || stopped) return;
      started = true;
      void cycle();
    },
    stop() {
      stopped = true;
      generation += 1;
      clearTimer();
      teardown();
    },
  };
}
